import React, { useState, useEffect, useRef } from 'react';
import api from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  CreditCard, 
  Plus, 
  Trash2, 
  Eye, 
  Camera,
  X,
  Pencil,
  QrCode
} from 'lucide-react';
import Barcode from 'react-barcode';
import { Html5Qrcode } from 'html5-qrcode';

// Predefined colors for cards (20 colors)
const CARD_COLORS = [
  // Riga 1 - Rossi e Rosa
  '#FF6B6B', '#EF4444', '#EC4899', '#F43F5E',
  // Riga 2 - Arancioni e Gialli
  '#F97316', '#F59E0B', '#F7B731', '#FFA07A',
  // Riga 3 - Verdi
  '#84CC16', '#22C55E', '#10B981', '#A3CB38',
  // Riga 4 - Azzurri e Blu
  '#06B6D4', '#0EA5E9', '#3B82F6', '#45B7D1',
  // Riga 5 - Viola e Altri
  '#8B5CF6', '#A855F7', '#6366F1', '#4ECDC4',
];

const LoyaltyCards = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [newCard, setNewCard] = useState({
    name: '',
    barcode: '',
    color: CARD_COLORS[0]
  });
  const [editCard, setEditCard] = useState({
    id: '',
    name: '',
    barcode: '',
    color: CARD_COLORS[0]
  });
  const [searchTerm, setSearchTerm] = useState('');
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  useEffect(() => {
    fetchCards();
  }, []);

  const fetchCards = async () => {
    try {
      const response = await api.get('/loyalty-cards');
      // Sort alphabetically by name
      const sortedCards = response.data.sort((a, b) => 
        a.name.localeCompare(b.name, 'it', { sensitivity: 'base' })
      );
      setCards(sortedCards);
    } catch (error) {
      toast.error('Errore nel caricamento delle tessere');
    } finally {
      setLoading(false);
    }
  };

  // Filter cards by search term
  const filteredCards = cards.filter(card =>
    card.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    card.barcode.includes(searchTerm)
  );

  const startScanner = async () => {
    setScannerOpen(true);
    
    setTimeout(async () => {
      try {
        const html5QrCode = new Html5Qrcode("barcode-reader");
        html5QrCodeRef.current = html5QrCode;
        
        await html5QrCode.start(
          { facingMode: "environment" },
          {
            fps: 10,
            qrbox: { width: 250, height: 150 },
            aspectRatio: 1.777
          },
          (decodedText) => {
            // Keep barcode as-is from scanner
            const barcodeStr = String(decodedText).trim();
            setNewCard(prev => ({ ...prev, barcode: barcodeStr }));
            stopScanner();
            toast.success('Barcode letto! Verifica che sia corretto.');
          },
          (errorMessage) => {
            // Ignore scan errors
          }
        );
      } catch (err) {
        console.error("Scanner error:", err);
        toast.error('Impossibile avviare la fotocamera');
        setScannerOpen(false);
      }
    }, 100);
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current = null;
      } catch (err) {
        console.error("Stop scanner error:", err);
      }
    }
    setScannerOpen(false);
  };

  const handleAddCard = async () => {
    if (!newCard.name.trim()) {
      toast.error('Inserisci il nome della tessera');
      return;
    }
    if (!newCard.barcode.trim()) {
      toast.error('Inserisci o scansiona il barcode');
      return;
    }

    try {
      await api.post('/loyalty-cards', newCard);
      toast.success('Tessera aggiunta con successo');
      setAddDialogOpen(false);
      setNewCard({ name: '', barcode: '', color: CARD_COLORS[Math.floor(Math.random() * CARD_COLORS.length)] });
      fetchCards();
    } catch (error) {
      toast.error('Errore nell\'aggiunta della tessera');
    }
  };

  const handleDeleteCard = async (cardId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa tessera?')) return;
    
    try {
      await api.delete(`/loyalty-cards/${cardId}`);
      toast.success('Tessera eliminata');
      fetchCards();
    } catch (error) {
      toast.error('Errore nell\'eliminazione della tessera');
    }
  };

  const openEditDialog = (card) => {
    setEditCard({
      id: card.id,
      name: card.name,
      barcode: card.barcode,
      color: card.color
    });
    setEditDialogOpen(true);
  };

  const handleEditCard = async () => {
    if (!editCard.name.trim()) {
      toast.error('Inserisci il nome della tessera');
      return;
    }
    if (!editCard.barcode.trim()) {
      toast.error('Inserisci il barcode');
      return;
    }

    try {
      await api.put(`/loyalty-cards/${editCard.id}`, {
        name: editCard.name,
        barcode: editCard.barcode,
        color: editCard.color
      });
      toast.success('Tessera modificata con successo');
      setEditDialogOpen(false);
      fetchCards();
    } catch (error) {
      toast.error('Errore nella modifica della tessera');
    }
  };

  const openViewDialog = (card) => {
    setSelectedCard(card);
    setViewDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D94E1F]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Tessere Fedeltà
          </h1>
          <p className="text-slate-500 mt-1">Gestisci le tue tessere fedeltà</p>
        </div>
        <Button
          onClick={() => {
            setNewCard({ name: '', barcode: '', color: CARD_COLORS[Math.floor(Math.random() * CARD_COLORS.length)] });
            setAddDialogOpen(true);
          }}
          className="bg-[#D94E1F] hover:bg-[#B83E15] text-white"
          data-testid="add-card-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuova Tessera
        </Button>
      </div>

      {/* Search */}
      {cards.length > 0 && (
        <div className="relative">
          <Input
            type="text"
            placeholder="Cerca tessera per nome o barcode..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="h-12 rounded-xl pl-4 pr-10"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      )}

      {/* Cards Grid */}
      {cards.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-100 p-12 text-center">
          <CreditCard className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">Nessuna tessera</h3>
          <p className="text-slate-500 mb-4">Aggiungi la tua prima tessera fedeltà</p>
          <Button
            onClick={() => setAddDialogOpen(true)}
            className="bg-[#D94E1F] hover:bg-[#B83E15] text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Aggiungi Tessera
          </Button>
        </div>
      ) : filteredCards.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-100 p-12 text-center">
          <CreditCard className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">Nessun risultato</h3>
          <p className="text-slate-500">Nessuna tessera trovata per "{searchTerm}"</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCards.map((card) => (
            <div
              key={card.id}
              className="relative rounded-xl overflow-hidden shadow-lg transition-transform hover:scale-[1.02]"
              style={{ backgroundColor: card.color }}
            >
              <div className="p-6 text-white">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <CreditCard className="w-8 h-8 mb-2 opacity-80" />
                    <h3 className="text-xl font-bold">{card.name}</h3>
                  </div>
                  <button
                    onClick={() => handleDeleteCard(card.id)}
                    className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex items-center justify-between mt-4">
                  <span className="text-sm opacity-80 font-mono">{card.barcode}</span>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => openEditDialog(card)}
                      variant="secondary"
                      size="sm"
                      className="bg-white/20 hover:bg-white/30 text-white border-0"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => openViewDialog(card)}
                      variant="secondary"
                      size="sm"
                      className="bg-white/20 hover:bg-white/30 text-white border-0"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Vedi
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Card Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={(open) => {
        if (!open) stopScanner();
        setAddDialogOpen(open);
      }}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-[#D94E1F]" />
              Nuova Tessera Fedeltà
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-700">Nome Tessera *</Label>
              <Input
                value={newCard.name}
                onChange={(e) => setNewCard({ ...newCard, name: e.target.value })}
                placeholder="es. Conad, Esselunga..."
                className="mt-1.5 h-12 rounded-xl"
                data-testid="card-name-input"
              />
            </div>

            <div>
              <Label className="text-slate-700">Barcode *</Label>
              <div className="flex gap-2 mt-1.5">
                <Input
                  type="text"
                  inputMode="text"
                  value={newCard.barcode}
                  onChange={(e) => setNewCard({ ...newCard, barcode: e.target.value })}
                  placeholder="Inserisci o scansiona"
                  className="h-12 rounded-xl flex-1 font-mono"
                  data-testid="card-barcode-input"
                />
                <Button
                  type="button"
                  onClick={startScanner}
                  className="h-12 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl"
                >
                  <Camera className="w-5 h-5" />
                </Button>
              </div>
              {newCard.barcode && (
                <div className="flex items-center gap-2 mt-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setNewCard({ ...newCard, barcode: '0' + newCard.barcode })}
                    className="text-xs"
                  >
                    + Aggiungi 0 iniziale
                  </Button>
                  <span className="text-xs text-slate-500">
                    Lunghezza: {newCard.barcode.length} caratteri
                  </span>
                </div>
              )}
            </div>

            {/* Scanner Area */}
            {scannerOpen && (
              <div className="relative">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-600">Inquadra il barcode...</span>
                  <button
                    onClick={stopScanner}
                    className="p-1 rounded-full bg-slate-100 hover:bg-slate-200"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div 
                  id="barcode-reader" 
                  className="rounded-xl overflow-hidden border border-slate-200"
                  style={{ width: '100%' }}
                />
              </div>
            )}

            <div>
              <Label className="text-slate-700">Colore Tessera</Label>
              <div className="grid grid-cols-5 gap-2 mt-2">
                {CARD_COLORS.map((color) => (
                  <div
                    key={color}
                    onClick={() => setNewCard({ ...newCard, color })}
                    className={`w-8 h-8 rounded-full transition-transform cursor-pointer border-2 ${
                      newCard.color === color ? 'ring-2 ring-offset-2 ring-slate-400 scale-110 border-slate-600' : 'border-slate-200 hover:border-slate-400'
                    }`}
                    style={{ backgroundColor: color }}
                    role="button"
                    tabIndex={0}
                  />
                ))}
              </div>
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-200">
                <span className="text-sm text-slate-500">Personalizzato:</span>
                <input
                  type="color"
                  value={newCard.color}
                  onChange={(e) => setNewCard({ ...newCard, color: e.target.value })}
                  className="w-10 h-10 rounded cursor-pointer border-2 border-slate-200"
                />
                <Input
                  type="text"
                  value={newCard.color}
                  onChange={(e) => setNewCard({ ...newCard, color: e.target.value })}
                  placeholder="#FF6B6B"
                  className="w-24 h-8 text-xs font-mono"
                />
              </div>
            </div>
          </div>

          <DialogFooter className="flex flex-col gap-2">
            <Button
              onClick={handleAddCard}
              className="w-full h-12 bg-[#D94E1F] hover:bg-[#B83E15] text-white rounded-xl"
              data-testid="save-card-btn"
            >
              Salva Tessera
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                stopScanner();
                setAddDialogOpen(false);
              }}
              className="w-full h-12 rounded-xl"
            >
              Annulla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Card Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <Pencil className="w-5 h-5 text-[#D94E1F]" />
              Modifica Tessera
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-700">Nome Tessera *</Label>
              <Input
                value={editCard.name}
                onChange={(e) => setEditCard({ ...editCard, name: e.target.value })}
                placeholder="es. Conad, Esselunga..."
                className="mt-1.5 h-12 rounded-xl"
              />
            </div>

            <div>
              <Label className="text-slate-700">Barcode *</Label>
              <Input
                type="text"
                inputMode="text"
                value={editCard.barcode}
                onChange={(e) => setEditCard({ ...editCard, barcode: e.target.value })}
                placeholder="Codice a barre"
                className="mt-1.5 h-12 rounded-xl font-mono"
              />
              {editCard.barcode && (
                <div className="flex items-center gap-2 mt-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setEditCard({ ...editCard, barcode: '0' + editCard.barcode })}
                    className="text-xs"
                  >
                    + Aggiungi 0 iniziale
                  </Button>
                  <span className="text-xs text-slate-500">
                    Lunghezza: {editCard.barcode.length} caratteri
                  </span>
                </div>
              )}
            </div>

            <div>
              <Label className="text-slate-700">Colore Tessera</Label>
              <div className="grid grid-cols-5 gap-2 mt-2">
                {CARD_COLORS.map((color) => (
                  <div
                    key={color}
                    onClick={() => setEditCard({ ...editCard, color })}
                    className={`w-8 h-8 rounded-full transition-transform cursor-pointer border-2 ${
                      editCard.color === color ? 'ring-2 ring-offset-2 ring-slate-400 scale-110 border-slate-600' : 'border-slate-200 hover:border-slate-400'
                    }`}
                    style={{ backgroundColor: color }}
                    role="button"
                    tabIndex={0}
                  />
                ))}
              </div>
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-200">
                <span className="text-sm text-slate-500">Personalizzato:</span>
                <input
                  type="color"
                  value={editCard.color}
                  onChange={(e) => setEditCard({ ...editCard, color: e.target.value })}
                  className="w-10 h-10 rounded cursor-pointer border-2 border-slate-200"
                />
                <Input
                  type="text"
                  value={editCard.color}
                  onChange={(e) => setEditCard({ ...editCard, color: e.target.value })}
                  placeholder="#FF6B6B"
                  className="w-24 h-8 text-xs font-mono"
                />
              </div>
            </div>
          </div>

          <DialogFooter className="flex flex-col gap-2">
            <Button
              onClick={handleEditCard}
              className="w-full h-12 bg-[#D94E1F] hover:bg-[#B83E15] text-white rounded-xl"
            >
              Salva Modifiche
            </Button>
            <Button
              variant="outline"
              onClick={() => setEditDialogOpen(false)}
              className="w-full h-12 rounded-xl"
            >
              Annulla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Card Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="bg-white max-w-lg">
          {selectedCard && (
            <>
              <DialogHeader>
                <DialogTitle 
                  className="text-2xl font-bold text-center"
                  style={{ color: selectedCard.color }}
                >
                  {selectedCard.name}
                </DialogTitle>
              </DialogHeader>
              
              <div className="py-6 flex flex-col items-center">
                <div className="bg-white p-4 rounded-xl shadow-inner border border-slate-100 w-full">
                  <Barcode 
                    value={String(selectedCard.barcode)}
                    format="CODE128"
                    width={2}
                    height={100}
                    fontSize={16}
                    margin={10}
                    displayValue={true}
                  />
                </div>
                <p className="text-slate-500 text-sm mt-4">
                  Mostra questo barcode alla cassa
                </p>
              </div>

              <DialogFooter>
                <Button
                  onClick={() => setViewDialogOpen(false)}
                  className="w-full h-12 rounded-xl"
                  style={{ backgroundColor: selectedCard.color }}
                >
                  Chiudi
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoyaltyCards;
